import { useState, type FormEvent } from 'react';
import { api } from '../../api';
import { ConfirmDialog, Dialog } from '../../components/Dialog';
import { useToast } from '../../lib/toast';
import { fehlerText } from '../tarifHinweis';
import { PERSONEN_MAX, personenUrl, personUrl, type KontaktArt, type Person } from './KontaktDaten';
import './kontakte.css';

interface Werte {
  name: string;
  funktion: string;
  email: string;
  phone: string;
  kopie: boolean;
}
const LEER: Werte = { name: '', funktion: '', email: '', phone: '', kopie: false };
const werteVon = (p: Person): Werte => ({
  name: p.name,
  funktion: p.funktion ?? '',
  email: p.email ?? '',
  phone: p.phone ?? '',
  kopie: p.kopie,
});
const telefon = (t: string) => `tel:${t.replace(/[^\d+]/g, '')}`;

// Karte „Ansprechpartner“ im Kunden- und Lieferanten-Detail: bis zu 20 Personen mit Aufgabe, E-Mail und Telefon, in
// ihrer Reihenfolge. Nur bei Kunden das Häkchen „in Kopie“ (der Server nimmt sie in „Kopie an“, nie in „An“). Ändern,
// Entfernen und Verschieben nur mit dem Recht (pflegen); ansehen darf jeder.
export function Personen({
  art,
  kontaktId,
  personen,
  pflegen,
  onChanged,
}: {
  art: KontaktArt;
  kontaktId: string;
  personen: Person[];
  pflegen: boolean;
  onChanged(): Promise<void>;
}) {
  const toast = useToast();
  // Fenster: null zu, 'neu' oder die Person, die geändert wird
  const [edit, setEdit] = useState<Person | 'neu' | null>(null);
  const [w, setW] = useState<Werte>(LEER);
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [weg, setWeg] = useState<Person | null>(null);

  function oeffnen(p: Person | 'neu') {
    setW(p === 'neu' ? LEER : werteVon(p));
    setFehler(null);
    setEdit(p);
  }

  async function speichern(e: FormEvent) {
    e.preventDefault();
    if (!w.name.trim()) return setFehler('Bitte den Namen eintragen.');
    const body = {
      name: w.name,
      funktion: w.funktion,
      email: w.email,
      phone: w.phone,
      kopie: art === 'kunde' && w.kopie && !!w.email.trim(),
    };
    setBusy(true);
    setFehler(null);
    try {
      if (edit === 'neu') await api.post(personenUrl(art, kontaktId), body);
      else if (edit) await api.put(personUrl(edit.id), body);
      await onChanged();
      toast('Ansprechpartner gespeichert');
      setEdit(null);
    } catch (err) {
      setFehler(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  async function entfernen(p: Person) {
    setBusy(true);
    try {
      await api.del(personUrl(p.id));
      await onChanged();
      toast(`Ansprechpartner „${p.name}“ entfernt`);
    } catch (err) {
      const t = fehlerText(err);
      if (t) toast(t);
    } finally {
      setBusy(false);
      setWeg(null);
    }
  }

  // eine Stelle nach oben: alle bekommen ihre Stelle in der neuen Folge (nur wer sich bewegt, wird gespeichert)
  async function hoch(i: number) {
    const neu = [...personen];
    [neu[i - 1], neu[i]] = [neu[i]!, neu[i - 1]!];
    setBusy(true);
    try {
      for (const [pos, p] of neu.entries()) {
        if (p.pos !== pos)
          await api.put(personUrl(p.id), { name: p.name, funktion: p.funktion, email: p.email, phone: p.phone, kopie: p.kopie, pos });
      }
      await onChanged();
    } catch (err) {
      const t = fehlerText(err);
      if (t) toast(t);
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rt-card kt-karte" aria-labelledby={`kt-personen-${art}`}>
      <div className="kt-karte-kopf">
        <h2 id={`kt-personen-${art}`}>Ansprechpartner</h2>
        {pflegen && personen.length < PERSONEN_MAX && (
          <button type="button" className="btn small" onClick={() => oeffnen('neu')}>
            + Ansprechpartner
          </button>
        )}
      </div>
      {personen.length === 0 ? (
        <p className="muted kt-leer">Noch keine Ansprechpartner.</p>
      ) : (
        <ul className="kt-liste">
          {personen.map((p, i) => (
            <li key={p.id} className="kt-person">
              <div className="kt-person-text">
                <b>{p.name}</b>
                {p.kopie && <span className="badge b-info">In Kopie</span>}
                {p.funktion && <span className="muted kt-funktion">{p.funktion}</span>}
                {p.email && (
                  <a href={`mailto:${p.email}`} className="kt-link">
                    {p.email}
                  </a>
                )}
                {p.phone && (
                  <a href={telefon(p.phone)} className="kt-link">
                    {p.phone}
                  </a>
                )}
              </div>
              {pflegen && (
                <div className="kt-zeile-act">
                  {i > 0 && (
                    <button
                      type="button"
                      className="linkbtn"
                      disabled={busy}
                      aria-label={`${p.name} nach oben`}
                      onClick={() => void hoch(i)}
                    >
                      ↑
                    </button>
                  )}
                  <button type="button" className="linkbtn" onClick={() => oeffnen(p)}>
                    Ändern
                  </button>
                  <button type="button" className="linkbtn rt-danger" onClick={() => setWeg(p)}>
                    Entfernen
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {art === 'kunde' && (
        <p className="rt-hint kt-klein">Auf Rechnungen steht weiter der Zusatz aus der Anschrift (z. B. „z. Hd. Frau Meier“).</p>
      )}

      <Dialog open={edit !== null} onClose={() => setEdit(null)} title="Ansprechpartner" className="kt-dlg">
        <form onSubmit={(e) => void speichern(e)} noValidate>
          <label className="field">
            <span>Name</span>
            <input value={w.name} maxLength={120} autoComplete="off" autoFocus onChange={(e) => setW({ ...w, name: e.target.value })} />
          </label>
          <label className="field">
            <span>Aufgabe oder Abteilung (optional)</span>
            <input
              value={w.funktion}
              maxLength={80}
              placeholder="z. B. Buchhaltung"
              onChange={(e) => setW({ ...w, funktion: e.target.value })}
            />
          </label>
          <label className="field">
            <span>E-Mail (optional)</span>
            <input
              type="email"
              value={w.email}
              maxLength={120}
              autoComplete="off"
              onChange={(e) => setW({ ...w, email: e.target.value, kopie: e.target.value.trim() ? w.kopie : false })}
            />
          </label>
          <label className="field">
            <span>Telefon (optional)</span>
            <input type="tel" value={w.phone} maxLength={40} autoComplete="off" onChange={(e) => setW({ ...w, phone: e.target.value })} />
          </label>
          {art === 'kunde' && (
            <>
              <label className="check kt-kopie">
                <input
                  type="checkbox"
                  checked={w.kopie}
                  disabled={!w.email.trim()}
                  onChange={(e) => setW({ ...w, kopie: e.target.checked })}
                />
                <span>Bekommt Belege per E-Mail in Kopie</span>
              </label>
              <p className="rt-hint kt-kopie-h">
                {w.email.trim()
                  ? 'Rechnungen, Angebote, Aufträge und Mahnungen, auch die automatischen.'
                  : 'Dafür bitte eine E-Mail eintragen.'}
              </p>
            </>
          )}
          {fehler && <div className="notice bad">{fehler}</div>}
          <div className="dlg-actions">
            <button type="button" className="btn" onClick={() => setEdit(null)}>
              Abbrechen
            </button>
            <button type="submit" className="btn primary" disabled={busy}>
              {busy ? 'Wird gespeichert …' : 'Speichern'}
            </button>
          </div>
        </form>
      </Dialog>
      <ConfirmDialog
        open={!!weg}
        title={weg ? `Ansprechpartner „${weg.name}“ entfernen?` : ''}
        confirmLabel="Ja, entfernen"
        danger
        busy={busy}
        onConfirm={() => weg && void entfernen(weg)}
        onCancel={() => setWeg(null)}
      />
    </section>
  );
}
